import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mcpCall } from '../lib/mcp';
import { Button, PageHeader, RefreshButton, SearchInput, Select, Toggle } from '../components/ui';
import {
  AlertCircle,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  Key,
  Plus,
  Power,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { themedConfirm } from '../lib/themed-dialogs';

interface KeyEntry {
  id: string;
  label: string;
  scopes: string[];
  permissions: string[]; // legacy
  enabled: boolean;
  created: string;
  last_used: string | null;
  use_count: number;
  key_preview: string;
  protected?: boolean;
  system_origin?: string | null;
}
interface Namespace {
  namespace: string;
  keys: KeyEntry[];
}
type TFunc = ReturnType<typeof useTranslation>['t'];

const keysApi = {
  list: () => mcpCall<{ ok: boolean; namespaces: Namespace[] }>('identity.api_key_list'),
  create: (body: { namespace: string; label: string; scopes: string[] }) =>
    mcpCall<{ ok: boolean; key: string; id: string; scopes: string[] }>('identity.api_key_create', body),
  revoke: (namespace: string, id: string) =>
    mcpCall<{ ok: boolean }>('identity.api_key_revoke', { namespace, id }),
  toggle: (namespace: string, id: string, enabled: boolean) =>
    mcpCall<{ ok: boolean; enabled: boolean }>('identity.api_key_toggle', { namespace, id, enabled }),
};

/** Guard against MCP/DB shape drift (string scopes, missing keys, non-array lists). */
function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function getScopes(k: KeyEntry): string[] {
  const scopes = asArray<string>(k.scopes).map(String).filter(Boolean);
  if (scopes.length) return scopes;
  const perms = asArray<string>(k.permissions);
  return perms.includes('write') ? ['*:read', '*:write'] : ['*:read'];
}

function timeAgo(iso: string | null, t: TFunc) {
  if (!iso) return '—';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 2) return t('keys.justNow', 'právě teď');
  if (m < 60) return t('keys.minutesAgo', '{{count}} min', { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('keys.hoursAgo', '{{count}} hod', { count: h });
  const d = Math.floor(h / 24);
  if (d < 30) return t('keys.daysAgo', '{{count}} dní', { count: d });
  return new Date(iso).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'short' });
}

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: '2-digit',
  });
}

function ScopeBadge({ scope }: { scope: string }) {
  const s = String(scope ?? '');
  const isWrite = s.endsWith(':write') || s === '*:write' || s === 'acm:admin';
  const isAdmin = s === 'acm:admin';
  const cls = isAdmin
    ? 'bg-red-500/15 text-red-400 border border-red-500/20'
    : isWrite
    ? 'bg-orange-500/15 text-orange-400'
    : 'bg-blue-500/15 text-blue-400';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-medium ${cls}`}>
      {s}
    </span>
  );
}

// ── New Key Modal ─────────────────────────────────────────────────────────────

function NewKeyModal({ namespaces, installedModules, onClose, onCreated }: {
  namespaces: string[];
  installedModules: string[];
  onClose: () => void;
  onCreated: (key: string, label: string, scopes: string[]) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    namespace: namespaces[0] ?? 'default',
    label: '',
    mode: 'modules' as 'modules' | 'global-read' | 'global-write' | 'admin',
  });
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set());
  const [modSearch, setModSearch] = useState('');

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  };

  const toggleAllForModule = (mod: string, checked: boolean) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(`${mod}:read`);
        next.add(`${mod}:write`);
      } else {
        next.delete(`${mod}:read`);
        next.delete(`${mod}:write`);
      }
      return next;
    });
  };

  const finalScopes = form.mode === 'admin'
    ? ['acm:admin']
    : form.mode === 'global-write'
    ? ['*:read', '*:write']
    : form.mode === 'global-read'
    ? ['*:read']
    : [...selectedScopes];

  const mut = useMutation({
    mutationFn: () =>
      keysApi.create({ namespace: form.namespace, label: form.label, scopes: finalScopes }),
    onSuccess: (r) => onCreated(r.key, form.label, r.scopes ?? finalScopes),
  });

  const inp =
    'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500';

  const visibleMods = modSearch
    ? installedModules.filter((m) => m.includes(modSearch.toLowerCase()))
    : installedModules;

  return (
    <div
      className='fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto'
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className='text-base font-bold text-white mb-4 flex items-center gap-2'>
          <Key size={15} className='text-blue-400' /> {t('keys.newKeyModalTitle', 'Nový API klíč')}
        </h2>

        <div className='space-y-4'>
          {/* Namespace + Label */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            <label className='block'>
              <div className='text-xs text-gray-400 mb-1'>{t('keys.namespaceLabel', 'Namespace')}</div>
              <Select
                value={form.namespace}
                onChange={(e) => setForm((f) => ({ ...f, namespace: e.target.value }))}
              >
                {namespaces.map((ns) => <option key={ns} value={ns}>{ns}</option>)}
                {!namespaces.includes('default') && <option value='default'>default</option>}
              </Select>
            </label>
            <label className='block'>
              <div className='text-xs text-gray-400 mb-1'>{t('keys.labelRequired', 'Label *')}</div>
              <input
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                placeholder={t('keys.labelPlaceholderExample', 'Fleet agent CI')}
                className={inp}
              />
            </label>
          </div>

          {/* Mode switch */}
          <div>
            <div className='text-xs text-gray-400 mb-2'>{t('keys.permissionType', 'Typ oprávnění')}</div>
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-1.5'>
              {([
                { id: 'modules', label: t('keys.modePerModule', 'Per-modul'), cls: 'blue' },
                { id: 'global-read', label: t('keys.modeGlobalRead', 'Global read'), cls: 'green' },
                { id: 'global-write', label: t('keys.modeGlobalWrite', 'Global write'), cls: 'orange' },
                { id: 'admin', label: t('keys.modeAdmin', 'Admin'), cls: 'red' },
              ] as const).map(({ id, label, cls }) => (
                <button
                  key={id}
                  onClick={() => setForm((f) => ({ ...f, mode: id }))}
                  className={`py-2 rounded-lg text-xs font-medium border transition-colors
                    ${
                    form.mode === id
                      ? `bg-${cls}-600/20 border-${cls}-500/30 text-${cls}-400`
                      : 'border-gray-700 text-gray-500 hover:text-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Per-modul scope výběr */}
          {form.mode === 'modules' && (
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <span className='text-xs text-gray-400'>{t('keys.selectScopes', 'Vyberte scopy:')}</span>
                {selectedScopes.size > 0 && (
                  <button
                    onClick={() => setSelectedScopes(new Set())}
                    className='text-[10px] text-gray-600 hover:text-gray-400 transition-colors'
                  >
                    {t('keys.deselectAll', 'Odznačit vše')}
                  </button>
                )}
              </div>
              {installedModules.length > 8 && (
                <SearchInput
                  value={modSearch}
                  onChange={setModSearch}
                  placeholder={t('keys.filterModulesPlaceholder', 'Filtrovat moduly…')}
                  size='sm'
                />
              )}
              <div className='space-y-1 max-h-64 overflow-y-auto pr-1'>
                {visibleMods.map((mod) => {
                  const hasRead = selectedScopes.has(`${mod}:read`);
                  const hasWrite = selectedScopes.has(`${mod}:write`);
                  const bothChecked = hasRead && hasWrite;
                  return (
                    <div
                      key={mod}
                      className={`rounded-lg border px-3 py-2 transition-colors ${
                        bothChecked
                          ? 'border-blue-700/40 bg-blue-600/5'
                          : 'border-gray-800 bg-gray-900/50'
                      }`}
                    >
                      <div className='flex items-center justify-between gap-3'>
                        <span className='text-xs font-medium text-white font-mono flex-1 min-w-0 truncate'>
                          {mod}
                        </span>
                        {/* Quick toggle for both */}
                        <button
                          onClick={() => toggleAllForModule(mod, !bothChecked)}
                          className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                            bothChecked
                              ? 'bg-blue-600/20 border-blue-500/30 text-blue-400'
                              : 'border-gray-700 text-gray-600 hover:text-gray-300'
                          }`}
                        >
                          {t('keys.readWriteToggle', 'R+W')}
                        </button>
                        <div className='flex gap-2.5 shrink-0'>
                          {[
                            { s: `${mod}:read`, l: 'read' },
                            { s: `${mod}:write`, l: 'write' },
                          ].map(({ s }) => (
                            <label key={s} className='flex items-center gap-1 cursor-pointer'>
                              <input
                                type='checkbox'
                                checked={selectedScopes.has(s)}
                                onChange={() =>
                                  toggleScope(s)}
                                className='rounded accent-blue-500 w-3 h-3'
                              />
                              <ScopeBadge scope={s} />
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {visibleMods.length === 0 && (
                  <p className='text-xs text-gray-600 text-center py-3'>{t('keys.noModules', 'Žádný modul')}</p>
                )}
              </div>
            </div>
          )}

          {form.mode === 'global-read' && (
            <div className='rounded-lg border border-green-500/20 bg-green-500/5 px-3 py-2.5 text-xs text-green-300'>
              {t(
                'keys.globalReadInfo',
                'Klíč bude mít přístup ke čtení ve všech modulech. Vhodné pro monitoring a read-only integrace.',
              )}
            </div>
          )}
          {form.mode === 'global-write' && (
            <div className='rounded-lg border border-orange-500/20 bg-orange-500/5 px-3 py-2.5 text-xs text-orange-300'>
              {t(
                'keys.globalWriteInfo',
                'Klíč bude mít plný přístup ke všem modulům. Používej jen pro admin nástroje.',
              )}
            </div>
          )}
          {form.mode === 'admin' && (
            <div className='rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2.5 text-xs text-red-300'>
              {t(
                'keys.adminInfo',
                'Klíč s plným admin přístupem (acm:admin). Pouze pro správce systému.',
              )}
            </div>
          )}

          {/* Výsledné scopy */}
          {finalScopes.length > 0 && (
            <div>
              <div className='text-xs text-gray-500 mb-1.5'>
                {t('keys.resultingScopes', 'Výsledné scopy ({{count}}):', { count: finalScopes.length })}
              </div>
              <div className='flex flex-wrap gap-1 max-h-20 overflow-y-auto'>
                {finalScopes.map((s) => <ScopeBadge key={s} scope={s} />)}
              </div>
            </div>
          )}
        </div>

        <div className='flex gap-2 mt-5'>
          <button
            onClick={onClose}
            className='flex-1 px-4 py-2 rounded-lg border border-gray-700 text-sm text-gray-400 hover:text-white transition-colors'
          >
            {t('keys.cancel', 'Zrušit')}
          </button>
          <button
            disabled={!form.label || finalScopes.length === 0 || mut.isPending}
            onClick={() => mut.mutate()}
            className='flex-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-sm font-semibold text-white transition-colors'
          >
            {mut.isPending ? t('keys.generating', 'Generuji…') : t('keys.createKey', 'Vytvořit klíč')}
          </button>
        </div>

        {mut.error && (
          <p className='mt-3 text-xs text-red-400 flex items-center gap-1.5'>
            <AlertCircle size={12} /> {(mut.error as Error).message}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Show-once Modal ───────────────────────────────────────────────────────────

function KeyCreatedModal({ apiKey, label, scopes, onClose }: {
  apiKey: string;
  label: string;
  scopes: string[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className='fixed inset-0 bg-black/70 flex items-center justify-center z-50'>
      <div className='bg-gray-900 border border-yellow-500/30 rounded-2xl p-6 w-full max-w-md shadow-2xl'>
        <div className='flex items-center gap-2 text-yellow-400 mb-1'>
          <AlertCircle size={16} />
          <span className='font-bold text-sm'>{t('keys.shownOnceWarning', 'Klíč se zobrazí pouze jednou!')}</span>
        </div>
        <p className='text-xs text-gray-500 mb-4'>
          {t('keys.shownOnceHint', 'Zkopírujte a uložte. Po zavření ho už nezobrazíme.')}
        </p>
        <p className='text-xs text-gray-400 mb-1'>{label}</p>
        <div className='flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 mb-3'>
          <code className='flex-1 text-xs text-green-400 font-mono break-all'>{apiKey}</code>
          <button
            onClick={copy}
            className='text-gray-400 hover:text-white transition-colors flex-shrink-0'
          >
            {copied ? <Check size={14} className='text-green-400' /> : <Copy size={14} />}
          </button>
        </div>
        <div className='flex flex-wrap gap-1 mb-4'>
          {scopes.map((s) => <ScopeBadge key={s} scope={s} />)}
        </div>
        <button
          onClick={onClose}
          className='w-full px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm font-semibold text-white transition-colors'
        >
          {t('keys.savedCloseButton', 'Uložil jsem klíč, zavřít')}
        </button>
      </div>
    </div>
  );
}

// ── Namespace Section ─────────────────────────────────────────────────────────

const KEYS_PAGE_SIZE = 80;

function NamespaceSection({ ns, onRevoke, onToggle }: {
  ns: Namespace;
  onRevoke: (ns: string, id: string) => void;
  onToggle: (ns: string, id: string, enabled: boolean) => Promise<void>;
}) {
  const { t } = useTranslation();
  const keys = Array.isArray(ns.keys) ? ns.keys : [];
  // Systémové / obří namespacy (_session má tisíce klíčů) NEotevírat defaultně —
  // dřív UI namountovalo 4000+ řádků a tab Identita→Klíče umřel.
  const [open, setOpen] = useState(() => {
    if (ns.namespace.startsWith('_')) return false;
    return keys.length > 0 && keys.length <= 40;
  });
  const [limit, setLimit] = useState(KEYS_PAGE_SIZE);

  const adminCount = keys.filter((k) => getScopes(k).includes('acm:admin')).length;
  const writeCount = keys.filter((k) => {
    const s = getScopes(k);
    return s.includes('*:write') || s.some((x) => typeof x === 'string' && x.endsWith(':write'));
  }).length;
  const disabledCount = keys.filter((k) => !k.enabled).length;
  const visibleKeys = open ? keys.slice(0, limit) : [];

  return (
    <div className='border border-gray-800 rounded-xl overflow-hidden'>
      <button
        onClick={() => setOpen((o) => !o)}
        className='w-full flex items-center gap-3 px-4 py-3 bg-gray-900/40 hover:bg-gray-800/30 transition-colors text-left'
      >
        {open
          ? <ChevronDown size={13} className='text-gray-500 shrink-0' />
          : <ChevronRight size={13} className='text-gray-500 shrink-0' />}
        <span className='font-mono font-semibold text-white text-sm'>{ns.namespace}</span>
        <div className='flex items-center gap-3 text-xs text-gray-500 ml-auto shrink-0'>
          {adminCount > 0 && (
            <span className='text-red-400 flex items-center gap-1'>
              <ShieldCheck size={10} />
              {adminCount}× {t('keys.adminWord', 'admin')}
            </span>
          )}
          {writeCount > 0 && (
            <span className='text-orange-400'>{writeCount}× {t('keys.writeWord', 'write')}</span>
          )}
          {disabledCount > 0 && (
            <span className='text-gray-600'>{disabledCount}× {t('keys.offWord', 'off')}</span>
          )}
          <span>{t('keys.keysCount', '{{count}} klíčů', { count: keys.length })}</span>
        </div>
      </button>

      {open && (
        <>
          {/* Desktop table */}
          <table className='w-full text-sm hidden md:table'>
            <thead>
              <tr className='border-t border-b border-gray-800 bg-gray-900/20'>
                {[
                  t('keys.colLabel', 'Label'),
                  t('keys.colScopes', 'Scopy'),
                  t('keys.colPrefix', 'Prefix'),
                  t('keys.colUsage', 'Použití'),
                  t('keys.colLastUsed', 'Naposledy'),
                  t('keys.colCreated', 'Vytvořen'),
                  t('keys.colStatus', 'Stav'),
                  '',
                ].map(
                  (h) => (
                    <th key={String(h)} className='text-left px-3 py-2 text-xs font-medium text-gray-600'>
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-800/40'>
              {keys.length === 0 && (
                <tr>
                  <td colSpan={8} className='px-4 py-6 text-center text-gray-600 text-xs'>
                    {t('keys.noKeysInNamespace', 'Žádné klíče')}
                  </td>
                </tr>
              )}
              {visibleKeys.map((k) => (
                <tr
                  key={k.id}
                  className={`hover:bg-gray-800/20 transition-colors group ${
                    !k.enabled ? 'opacity-50' : ''
                  }`}
                >
                  <td className='px-3 py-2.5'>
                    <span className='text-white text-xs font-medium'>{k.label}</span>
                  </td>
                  <td className='px-3 py-2.5'>
                    <div className='flex flex-wrap gap-1'>
                      {getScopes(k).map((s) => <ScopeBadge key={s} scope={s} />)}
                    </div>
                  </td>
                  <td className='px-3 py-2.5'>
                    <code className='text-xs text-gray-400 font-mono'>{k.key_preview}</code>
                  </td>
                  <td className='px-3 py-2.5 text-xs text-gray-500'>{k.use_count}×</td>
                  <td className='px-3 py-2.5'>
                    <span className='flex items-center gap-1 text-xs text-gray-500'>
                      <Clock size={10} />
                      {timeAgo(k.last_used, t)}
                    </span>
                  </td>
                  <td className='px-3 py-2.5'>
                    <span className='flex items-center gap-1 text-xs text-gray-600' title={k.created}>
                      <Calendar size={10} />
                      {fmtDate(k.created)}
                    </span>
                  </td>
                  <td className='px-3 py-2.5'>
                    <Toggle
                      value={k.enabled}
                      onSave={(v) => onToggle(ns.namespace, k.id, v)}
                    />
                  </td>
                  <td className='px-3 py-2.5'>
                    {k.protected
                      ? (
                        <span
                          className='inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20'
                          title={t(
                            'keys.systemKeyTitle',
                            'System klíč ({{origin}}) — chráněn před smazáním',
                            { origin: k.system_origin ?? t('keys.unknownSource', 'neznámý zdroj') },
                          )}
                        >
                          🔒 {t('keys.systemBadge', 'system')}
                        </span>
                      )
                      : (
                        <div className='opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1'>
                          <button
                            onClick={async () => {
                              if (
                                await themedConfirm(
                                  t('keys.confirmDeleteKey', 'Smazat klíč "{{label}}"?', { label: k.label }),
                                )
                              ) onRevoke(ns.namespace, k.id);
                            }}
                            className='p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors'
                            title={t('keys.deleteKeyTooltip', 'Smazat klíč')}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                  </td>
                </tr>
              ))}
              {open && keys.length > limit && (
                <tr>
                  <td colSpan={8} className='px-4 py-3 text-center'>
                    <button
                      type='button'
                      onClick={() => setLimit((n) => n + KEYS_PAGE_SIZE)}
                      className='text-xs text-blue-400 hover:text-blue-300'
                    >
                      {t('keys.showMoreKeys', 'Zobrazit další ({{shown}} / {{total}})', {
                        shown: limit,
                        total: keys.length,
                      })}
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Phone cards */}
          <div className='md:hidden space-y-2 p-2 border-t border-gray-800'>
            {keys.length === 0 && (
              <div className='py-6 text-center text-gray-600 text-xs'>
                {t('keys.noKeysInNamespace', 'Žádné klíče')}
              </div>
            )}
            {visibleKeys.map((k) => (
              <div
                key={k.id}
                className={`rounded-xl border border-gray-800 bg-gray-950/50 p-3 space-y-2 ${
                  !k.enabled ? 'opacity-50' : ''
                }`}
              >
                <div className='flex items-start justify-between gap-2'>
                  <div className='min-w-0'>
                    <div className='text-sm font-medium text-white truncate'>{k.label}</div>
                    <code className='text-[11px] text-gray-500 font-mono'>{k.key_preview}</code>
                  </div>
                  <Toggle value={k.enabled} onSave={(v) => onToggle(ns.namespace, k.id, v)} />
                </div>
                <div className='flex flex-wrap gap-1'>
                  {getScopes(k).map((s) => <ScopeBadge key={s} scope={s} />)}
                </div>
                <div className='flex items-center justify-between text-[11px] text-gray-500'>
                  <span className='inline-flex items-center gap-1'>
                    <Clock size={10} />
                    {timeAgo(k.last_used, t)}
                  </span>
                  <span>{k.use_count}×</span>
                  {k.protected
                    ? (
                      <span className='text-amber-400 text-[10px]'>
                        🔒 {t('keys.systemBadge', 'system')}
                      </span>
                    )
                    : (
                      <button
                        type='button'
                        onClick={async () => {
                          if (
                            await themedConfirm(
                              t('keys.confirmDeleteKey', 'Smazat klíč "{{label}}"?', { label: k.label }),
                            )
                          ) onRevoke(ns.namespace, k.id);
                        }}
                        className='p-1.5 rounded-lg text-red-400/80 hover:bg-red-500/10'
                        title={t('keys.deleteKeyTooltip', 'Smazat klíč')}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                </div>
              </div>
            ))}
            {keys.length > limit && (
              <button
                type='button'
                onClick={() => setLimit((n) => n + KEYS_PAGE_SIZE)}
                className='w-full py-2 text-xs text-blue-400 hover:text-blue-300'
              >
                {t('keys.showMoreKeys', 'Zobrazit další ({{shown}} / {{total}})', {
                  shown: limit,
                  total: keys.length,
                })}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function Keys() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [modal, setModal] = useState<
    null | 'new' | { type: 'created'; key: string; label: string; scopes: string[] }
  >(null);
  const [filterScope, setFilterScope] = useState(searchParams.get('module') ?? '');
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['acm-keys'],
    queryFn: async () => {
      const r = await keysApi.list();
      // Normalize: never let non-array namespaces/keys reach render.
      const namespaces = asArray<Namespace>((r as { namespaces?: unknown })?.namespaces).map(
        (ns) => ({
          namespace: String((ns as Namespace)?.namespace ?? 'default'),
          keys: asArray<KeyEntry>((ns as Namespace)?.keys),
        }),
      );
      return { ok: true as const, namespaces };
    },
  });

  // Načíst seznam modulů ze serveru (namísto hardcoded seznamu)
  const { data: toolsData } = useQuery({
    queryKey: ['acm-tools-modules'],
    queryFn: async () => {
      const r = await mcpCall<unknown>('acm-self.modules_list');
      // Accept { modules: [] } or bare array
      if (Array.isArray(r)) return { modules: r as Array<{ name: string }> };
      const mods = (r as { modules?: unknown })?.modules;
      return { modules: asArray<{ name: string }>(mods) };
    },
    staleTime: 5 * 60 * 1000,
  });

  const revokeMut = useMutation({
    mutationFn: ({ namespace, id }: { namespace: string; id: string }) =>
      keysApi.revoke(namespace, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['acm-keys'] }),
  });

  const toggleMut = useMutation({
    mutationFn: ({ namespace, id, enabled }: { namespace: string; id: string; enabled: boolean }) =>
      keysApi.toggle(namespace, id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['acm-keys'] }),
  });

  const namespaces = asArray<Namespace>(data?.namespaces);

  const totalKeys = namespaces.reduce((s, ns) => s + asArray(ns.keys).length, 0);
  const enabledKeys = namespaces.reduce(
    (s, ns) => s + asArray(ns.keys).filter((k) => k.enabled).length,
    0,
  );
  const adminKeys = namespaces.reduce(
    (s, ns) => s + asArray(ns.keys).filter((k) => getScopes(k).includes('acm:admin')).length,
    0,
  );
  const writeKeys = namespaces.reduce((s, ns) =>
    s + asArray(ns.keys).filter((k) => {
      const sc = getScopes(k);
      return sc.includes('*:write') || sc.some((x) => x.endsWith(':write'));
    }).length, 0);

  // Moduly ze serveru — fallback na moduly z existujících klíčů
  const rawMods = asArray<{ name?: string } | string>(toolsData?.modules);
  const installedModules: string[] = rawMods.length
    ? [
      ...new Set(
        rawMods
          .map((module) => typeof module === 'string' ? module : module?.name)
          .filter((n): n is string => !!n),
      ),
    ].sort()
    : [
      ...new Set(
        namespaces.flatMap((ns) =>
          asArray(ns.keys).flatMap((k) => getScopes(k).map((s) => s.split(':')[0]))
        )
          .filter((m) => m && m !== '*' && m !== 'acm'),
      ),
    ].sort();

  // Pro filtraci — moduly s existujícími klíči (pro zvýraznění)
  const activeModules = new Set(
    namespaces.flatMap((ns) =>
      asArray(ns.keys).flatMap((k) => getScopes(k).map((s) => s.split(':')[0]))
    )
      .filter((m) => m && m !== '*' && m !== 'acm'),
  );

  const q = search.toLowerCase().trim();
  const filtered = namespaces
    .map((ns) => ({
      ...ns,
      keys: asArray(ns.keys).filter((k) => {
        const scopes = getScopes(k);
        const matchesScope = !filterScope ||
          ns.namespace === filterScope ||
          scopes.some((s) =>
            s.startsWith(filterScope + ':') || s === `${filterScope}:read` ||
            s === `${filterScope}:write`
          ) ||
          scopes.includes('*:read') || scopes.includes('*:write');
        const matchesSearch = !q ||
          String(k.label ?? '').toLowerCase().includes(q) ||
          String(ns.namespace ?? '').toLowerCase().includes(q) ||
          scopes.some((s) => s.toLowerCase().includes(q));
        return matchesScope && matchesSearch;
      }),
    }))
    .filter((ns) => ns.keys.length > 0);

  const filteredTotal = filtered.reduce((s, ns) => s + ns.keys.length, 0);

  return (
    <div className='space-y-4 sm:space-y-5 p-3 sm:p-6 min-w-0'>
      <PageHeader
        title={t('keys.title', 'API klíče')}
        description={t('keys.summaryLine', {
          total: totalKeys,
          // NE `ns` — i18next to bere jako option namespace a volá ns.forEach → crash
          nsCount: namespaces.length,
          active: enabledKeys,
          defaultValue: '{{total}} klíčů · {{nsCount}} namespaců · {{active}} aktivních',
        })}
        icon={Key}
        actions={
          <>
            <RefreshButton
              onRefresh={() => qc.invalidateQueries({ queryKey: ['acm-keys'] })}
              size='md'
            />
            <Button
              variant='primary'
              size='sm'
              icon={<Plus size={14} />}
              onClick={() => setModal('new')}
            >
              {t('keys.newKeyButton', 'Nový klíč')}
            </Button>
          </>
        }
      />

      {/* Stats — 2×2 na telefonu */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4'>
        {[
          { label: t('keys.statTotalKeys', 'Celkem klíčů'), value: totalKeys, color: 'text-white' },
          { label: t('keys.statActive', 'Aktivních'), value: enabledKeys, color: 'text-green-400' },
          { label: t('keys.statWriteKeys', 'Write klíče'), value: writeKeys, color: 'text-orange-400' },
          { label: t('keys.statAdminKeys', 'Admin klíče'), value: adminKeys, color: 'text-red-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className='bg-gray-900/40 border border-gray-800 rounded-xl p-3 sm:p-4'>
            <div className={`text-xl sm:text-2xl font-bold ${color}`}>{value}</div>
            <div className='text-[11px] sm:text-xs text-gray-500 mt-0.5'>{label}</div>
          </div>
        ))}
      </div>

      {/* Hledání */}
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder={t('keys.searchPlaceholder', 'Hledat klíč, namespace, scope…')}
        size='md'
        resultCount={q ? filteredTotal : undefined}
        totalCount={q ? totalKeys : undefined}
      />

      {/* Filtr podle modulu */}
      {installedModules.length > 0 && (
        <div className='flex items-center gap-2 flex-wrap'>
          <span className='text-xs text-gray-500 shrink-0'>{t('keys.moduleFilterLabel', 'Modul:')}</span>
          <button
            onClick={() => setFilterScope('')}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
              !filterScope
                ? 'bg-blue-600/20 text-blue-400 border-blue-500/30'
                : 'text-gray-500 hover:text-white border-gray-800'
            }`}
          >
            {t('keys.allFilter', 'Vše')}
          </button>
          {installedModules.map((m) => {
            const hasKeys = activeModules.has(m);
            return (
              <button
                key={m}
                onClick={() => setFilterScope(m === filterScope ? '' : m)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                  filterScope === m
                    ? 'bg-purple-600/20 text-purple-400 border-purple-500/30'
                    : hasKeys
                    ? 'text-gray-300 border-gray-700 hover:text-white hover:border-gray-500'
                    : 'text-gray-600 border-gray-800 hover:text-gray-400'
                }`}
              >
                {m}
                {hasKeys && <span className='ml-1 text-[9px] text-gray-500'>•</span>}
              </button>
            );
          })}
        </div>
      )}

      {isLoading && (
        <div className='py-12 text-center text-gray-500 text-sm'>{t('keys.loading', 'Načítám…')}</div>
      )}
      {error && (
        <div className='flex gap-2 items-center text-red-400 text-sm p-4 bg-red-500/10 rounded-xl border border-red-500/20'>
          <AlertCircle size={14} />
          {(error as Error).message}
        </div>
      )}

      {/* Výsledky / prázdný stav */}
      {!isLoading && !error && (
        <>
          {filtered.length === 0 && (q || filterScope)
            ? (
              <div className='py-12 text-center text-gray-600 text-sm'>
                <Key size={24} className='mx-auto mb-2 text-gray-700' />
                <p>{t('keys.noKeysMatchFilter', 'Žádné klíče neodpovídají filtru')}</p>
                {(q || filterScope) && (
                  <button
                    onClick={() => {
                      setSearch('');
                      setFilterScope('');
                    }}
                    className='mt-2 text-xs text-blue-400 hover:text-blue-300 transition-colors'
                  >
                    {t('keys.clearFilters', 'Zrušit filtry')}
                  </button>
                )}
              </div>
            )
            : (
              <div className='space-y-2'>
                {filtered.map((ns) => (
                  <NamespaceSection
                    key={ns.namespace}
                    ns={ns}
                    onRevoke={(namespace, id) => revokeMut.mutate({ namespace, id })}
                    onToggle={async (namespace, id, enabled) => {
                      await toggleMut.mutateAsync({ namespace, id, enabled });
                    }}
                  />
                ))}
              </div>
            )}
        </>
      )}

      {/* Power indicator v zápatí */}
      {!isLoading && totalKeys > 0 && totalKeys !== enabledKeys && (
        <div className='flex items-center gap-2 text-xs text-gray-600 px-1'>
          <Power size={10} />
          <span>{t('keys.disabledCount', '{{count}} klíčů je deaktivováno', { count: totalKeys - enabledKeys })}</span>
        </div>
      )}

      {modal === 'new' && (
        <NewKeyModal
          namespaces={namespaces.map((n) => n.namespace)}
          installedModules={installedModules}
          onClose={() => setModal(null)}
          onCreated={(key, label, scopes) => {
            qc.invalidateQueries({ queryKey: ['acm-keys'] });
            setModal({ type: 'created', key, label, scopes });
          }}
        />
      )}
      {modal && typeof modal === 'object' && modal.type === 'created' && (
        <KeyCreatedModal
          apiKey={modal.key}
          label={modal.label}
          scopes={modal.scopes}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
